import axios from 'axios';

const API_BASE_URL = 'https://impactstoriesfund.org/backend/public/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Helper to safely store token
const storeToken = (token) => {
  if (token && token !== 'null' && token !== 'undefined') {
    // Store under both names for compatibility
    localStorage.setItem('admin_token', token);
    localStorage.setItem('token', token);
    console.log('Tokens stored');
  } else {
    console.error('Invalid token received:', token);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('token');
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    // Debug logging
    console.log('API Request:', {
      url: config.url,
      hasToken: !!token,
      tokenType: typeof token,
      tokenValue: token ? token.substring(0, 20) + '...' : 'none'
    });
    
    if (token && token !== 'null' && token !== 'undefined') {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No valid token found for request');
    }
    
    // For multipart requests (file upload), also add token as form field
    if (config.data instanceof FormData && token) {
      config.data.append('_token', token);
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log('API Response success:', response.config.url);
    return response;
  },
  (error) => {
    console.error('API Response error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.response?.data?.message
    });
    
    if (error.response?.status === 401) {
      console.log('401 Unauthorized - clearing token');
      localStorage.removeItem('token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('token_expires_at');
      
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (credentials) => {
    console.log('Login attempt for:', credentials.email);
    return api.post('/login', credentials).then(response => {
      console.log('Login response:', response.data);
      
      if (response.data.token) {
        storeToken(response.data.token);
        localStorage.setItem('admin_user', JSON.stringify(response.data.user));
        localStorage.setItem('token_expires_at', response.data.expires_at);
        console.log('Token stored successfully');
      } else {
        console.error('No token in login response:', response.data);
      }
      
      return response;
    }).catch(error => {
      console.error('Login error:', error);
      throw error;
    });
  },
  
  logout: () => {
    console.log('Logout called');
    return api.post('/logout').finally(() => {
      localStorage.clear();
      console.log('LocalStorage cleared');
    });
  },
};

// Film API
export const filmAPI = {
  getAllFilms: () => {
    console.log('Fetching all films');
    return api.get('/films');
  },

  getFilm: (id) => api.get(`/films/${id}`),
  
  createFilm: (filmData) => {
    console.log('Creating film:', filmData.title);
    
    const formData = new FormData();
    formData.append('title', filmData.title);
    formData.append('description', filmData.description);
    formData.append('youtube_link', filmData.youtube_link);
    
    // Get token and add to form data
    const token = localStorage.getItem('token');
    if (token && token !== 'null' && token !== 'undefined') {
      formData.append('_token', token);
      console.log('Token added to form data');
    } else {
      console.error('No valid token available for film upload');
    }
    
    if (filmData.cover_image) {
      formData.append('cover_image', filmData.cover_image);
      console.log('Cover image added to form data');
    }
    
    // Log form data contents (excluding file content)
    console.log('FormData entries:');
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ', pair[0] === 'cover_image' ? '[FILE]' : pair[1]);
    }
    
    return api.post('/films', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  updateFilm: (id, filmData) => {
    const formData = new FormData();
    if (filmData.title) formData.append('title', filmData.title);
    if (filmData.description) formData.append('description', filmData.description);
    if (filmData.youtube_link) formData.append('youtube_link', filmData.youtube_link);
    if (filmData.cover_image) {
      formData.append('cover_image', filmData.cover_image);
    }
    
    // Use PUT method for update
    return api.put(`/films/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  deleteFilm: (id) => api.delete(`/films/${id}`),
};

export default api;